import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  Divider,
  Drawer,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableContainer,
  TablePagination,
  Paper,
  TableRow,
  TableSortLabel,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import OpenInFullRoundedIcon from '@mui/icons-material/OpenInFullRounded';
import { alpha, useTheme } from '@mui/material/styles';
import {
  incomeVerificationDashboardApi,
  type IncomeDashboardDetail,
  type IncomeDashboardListItem,
  type IncomeGateKey,
} from '../api/incomeVerificationDashboardApi';
import { messageFromAxiosErrorPayload } from '../lib/axiosErrorMessage';
import {
  incomeDepositPayorLabel,
  incomeDepositPayorRawCaption,
} from '../lib/incomeDepositPayorDisplay';
import {
  deriveBankRollup,
  deriveDepositEligibility,
  derivePayorGroups,
  downloadCsv,
  toCsv,
} from './incomeDashboardDerived';

function incomeError(err: unknown): string {
  const ax = err as {
    response?: { status?: number; data?: unknown };
    message?: string;
  };
  const fromBody = messageFromAxiosErrorPayload(ax.response?.data);
  if (fromBody) return fromBody;
  if (typeof ax.message === 'string' && ax.message.trim()) return ax.message;
  const status = ax.response?.status;
  if (status === 401) return 'Unauthorized. Sign in again.';
  if (status === 403) return 'Forbidden. Admin token is missing or invalid.';
  if (status === 404) return 'Record not found.';
  if (typeof status === 'number') return `Request failed (${status}).`;
  return 'Request failed.';
}

function gateColor(raw: string): 'success' | 'error' | 'warning' | 'default' {
  const u = raw.trim().toUpperCase();
  if (u === 'PASS') return 'success';
  if (u === 'FAIL') return 'error';
  if (u === 'REVIEW') return 'warning';
  return 'default';
}

function statusColor(raw: string): 'success' | 'error' | 'warning' | 'default' {
  const u = raw.trim().toUpperCase();
  if (u === 'VERIFIED') return 'success';
  if (u === 'REJECTED') return 'error';
  if (u === 'MANUAL_REVIEW') return 'warning';
  return 'default';
}

function formatIso(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleString();
}

function formatCad(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency: 'CAD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

const GATES: IncomeGateKey[] = ['RECURRENCE', 'STABILITY', 'ARBITRATION'];

function txDirectionColor(dir: string): 'success' | 'default' | 'warning' {
  if (dir === 'CREDIT') return 'success';
  if (dir === 'DEBIT') return 'default';
  return 'warning';
}

type BankSortCol = 'postedAt' | 'direction' | 'amount' | 'institution' | 'account' | 'summary';
type DepositSortCol = 'depositDate' | 'amount' | 'payor' | 'eligible' | 'summary';

function bankTxSortKey(
  t: IncomeDashboardDetail['bankTransactions'][number],
  col: BankSortCol,
): string | number {
  switch (col) {
    case 'postedAt':
      return new Date(t.postedAt).getTime();
    case 'direction':
      return t.direction;
    case 'amount':
      return t.amount ?? 0;
    case 'institution':
      return (t.institutionName ?? t.institutionCode ?? '').toLowerCase();
    case 'account':
      return (t.accountExternalId ?? '').toLowerCase();
    case 'summary':
      return `${t.summaryLine} ${t.description ?? ''}`.toLowerCase();
    default:
      return '';
  }
}

function depositSortKey(
  d: IncomeDashboardDetail['incomeDeposits'][number],
  col: DepositSortCol,
): string | number {
  switch (col) {
    case 'depositDate':
      return new Date(d.depositDate).getTime();
    case 'amount':
      return d.amount ?? 0;
    case 'payor':
      return `${d.payorDisplay ?? ''} ${d.payorType}`.toLowerCase();
    case 'eligible':
      return d.isEligible ? 1 : 0;
    case 'summary':
      return `${d.summaryLine} ${d.exclusionReason ?? ''}`.toLowerCase();
    default:
      return '';
  }
}

function bankMatchesQuery(
  t: IncomeDashboardDetail['bankTransactions'][number],
  q: string,
): boolean {
  const n = q.trim().toLowerCase();
  if (!n) return true;
  const blob = [
    t.postedAt,
    t.direction,
    String(t.amount ?? ''),
    t.institutionName,
    t.institutionCode,
    t.accountExternalId,
    t.summaryLine,
    t.description,
    t.invalidReason,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return blob.includes(n);
}

function depositMatchesQuery(
  d: IncomeDashboardDetail['incomeDeposits'][number],
  q: string,
): boolean {
  const n = q.trim().toLowerCase();
  if (!n) return true;
  const blob = [
    d.depositDate,
    String(d.amount ?? ''),
    d.payorDisplay,
    d.payorType,
    d.summaryLine,
    d.exclusionReason,
    d.isEligible ? 'eligible' : 'excluded',
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return blob.includes(n);
}

function BankTransactionsSection({ data }: { data: IncomeDashboardDetail }) {
  const txs = data.bankTransactions ?? [];
  const rollup = useMemo(() => deriveBankRollup(txs), [txs]);
  const [query, setQuery] = useState('');
  const [dirFilter, setDirFilter] = useState<'ALL' | 'CREDIT' | 'DEBIT' | 'NEUTRAL'>('ALL');
  const [sortCol, setSortCol] = useState<BankSortCol>('postedAt');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const rows = useMemo(() => {
    let list = txs.filter((t) => bankMatchesQuery(t, query));
    if (dirFilter !== 'ALL') list = list.filter((t) => t.direction === dirFilter);
    const mult = sortDir === 'asc' ? 1 : -1;
    return [...list].sort((a, b) => {
      const va = bankTxSortKey(a, sortCol);
      const vb = bankTxSortKey(b, sortCol);
      if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * mult;
      return String(va).localeCompare(String(vb)) * mult;
    });
  }, [txs, query, dirFilter, sortCol, sortDir]);

  const toggleSort = (col: BankSortCol) => {
    if (sortCol === col) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortCol(col);
      setSortDir(col === 'postedAt' ? 'desc' : 'asc');
    }
  };

  const exportCsv = () => {
    const headers = ['Posted', 'Direction', 'Amount', 'Institution', 'Account', 'Summary', 'Description'];
    const body = rows.map((t) => [
      t.postedAt,
      t.direction,
      t.amount ?? '',
      t.institutionName ?? t.institutionCode ?? '',
      t.accountExternalId ?? '',
      t.summaryLine,
      t.description ?? '',
    ]);
    downloadCsv(`bank-transactions-${data.id}.csv`, toCsv(headers, body));
  };

  return (
    <>
      {txs.length > 0 ? (
        <Card variant="outlined" sx={{ mb: 1.5 }}>
          <CardContent sx={{ py: 1.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
              Bank transaction rollups (this view)
            </Typography>
            <Grid container spacing={1}>
              <Grid size={{ xs: 6, sm: 3 }}>
                <Typography variant="caption" color="text.secondary">
                  Credits
                </Typography>
                <Typography variant="body2">
                  {rollup.creditCount} tx · {formatCad(rollup.creditSum)}
                </Typography>
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <Typography variant="caption" color="text.secondary">
                  Debits
                </Typography>
                <Typography variant="body2">
                  {rollup.debitCount} tx · {formatCad(rollup.debitSum)}
                </Typography>
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <Typography variant="caption" color="text.secondary">
                  Neutral
                </Typography>
                <Typography variant="body2">{rollup.neutralCount} tx</Typography>
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <Typography variant="caption" color="text.secondary">
                  Showing rows
                </Typography>
                <Typography variant="body2">
                  {rows.length} of {txs.length}
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      ) : null}

      {data.transactionMeta?.bankTransactionsCapped ? (
        <Alert severity="info" sx={{ mb: 1 }}>
          Showing the most recent {data.transactionMeta.bankTransactionsReturned} transactions (server cap).
        </Alert>
      ) : null}

      {txs.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No raw bank transactions are stored for this session (IBV fetch may be missing or empty).
        </Typography>
      ) : (
        <>
          <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 1.5 }} alignItems="center">
            <TextField
              size="small"
              label="Search table"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              sx={{ minWidth: 200, flex: '1 1 200px' }}
            />
            <ToggleButtonGroup
              size="small"
              exclusive
              value={dirFilter}
              onChange={(_, v) => v && setDirFilter(v)}
              aria-label="Filter by direction"
            >
              <ToggleButton value="ALL">All</ToggleButton>
              <ToggleButton value="CREDIT">Credits</ToggleButton>
              <ToggleButton value="DEBIT">Debits</ToggleButton>
              <ToggleButton value="NEUTRAL">Neutral</ToggleButton>
            </ToggleButtonGroup>
            <Button variant="outlined" size="small" onClick={exportCsv}>
              Export CSV ({rows.length})
            </Button>
          </Stack>
          <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 420 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sortDirection={sortCol === 'postedAt' ? sortDir : false}>
                    <TableSortLabel
                      active={sortCol === 'postedAt'}
                      direction={sortCol === 'postedAt' ? sortDir : 'asc'}
                      onClick={() => toggleSort('postedAt')}
                    >
                      Posted
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={sortCol === 'direction' ? sortDir : false}>
                    <TableSortLabel
                      active={sortCol === 'direction'}
                      direction={sortCol === 'direction' ? sortDir : 'asc'}
                      onClick={() => toggleSort('direction')}
                    >
                      Flow
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right" sortDirection={sortCol === 'amount' ? sortDir : false}>
                    <TableSortLabel
                      active={sortCol === 'amount'}
                      direction={sortCol === 'amount' ? sortDir : 'asc'}
                      onClick={() => toggleSort('amount')}
                      sx={{ justifyContent: 'flex-end' }}
                    >
                      Amount
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={sortCol === 'institution' ? sortDir : false}>
                    <TableSortLabel
                      active={sortCol === 'institution'}
                      direction={sortCol === 'institution' ? sortDir : 'asc'}
                      onClick={() => toggleSort('institution')}
                    >
                      Institution
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={sortCol === 'account' ? sortDir : false}>
                    <TableSortLabel
                      active={sortCol === 'account'}
                      direction={sortCol === 'account' ? sortDir : 'asc'}
                      onClick={() => toggleSort('account')}
                    >
                      Account
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ minWidth: 240 }} sortDirection={sortCol === 'summary' ? sortDir : false}>
                    <TableSortLabel
                      active={sortCol === 'summary'}
                      direction={sortCol === 'summary' ? sortDir : 'asc'}
                      onClick={() => toggleSort('summary')}
                    >
                      Summary
                    </TableSortLabel>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((t) => (
                  <TableRow key={t.id} hover selected={t.isInvalid}>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatIso(t.postedAt)}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={t.direction}
                        color={txDirectionColor(t.direction)}
                        variant={t.direction === 'NEUTRAL' ? 'outlined' : 'filled'}
                      />
                    </TableCell>
                    <TableCell align="right">{formatCad(t.amount)}</TableCell>
                    <TableCell>
                      <Typography variant="body2">{t.institutionName ?? t.institutionCode ?? '—'}</Typography>
                      {t.batchFetchStatus ? (
                        <Typography variant="caption" color="text.secondary" display="block">
                          fetch {t.batchFetchStatus}
                        </Typography>
                      ) : null}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'ui-monospace, monospace', fontSize: '0.75rem' }}>
                        {t.accountExternalId ?? '—'}
                      </Typography>
                      {t.accountType ? (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {t.accountType}
                        </Typography>
                      ) : null}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {t.summaryLine}
                      </Typography>
                      {t.description && t.description !== t.summaryLine ? (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {t.description}
                        </Typography>
                      ) : null}
                      {t.isInvalid ? (
                        <Chip size="small" color="warning" label={t.invalidReason || 'Invalid'} sx={{ mt: 0.5 }} />
                      ) : null}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </>
  );
}

function IncomeDepositsSection({ data }: { data: IncomeDashboardDetail }) {
  const deposits = data.incomeDeposits ?? [];
  const eligibility = useMemo(() => deriveDepositEligibility(deposits), [deposits]);
  const payorGroups = useMemo(() => derivePayorGroups(deposits), [deposits]);

  const [query, setQuery] = useState('');
  const [sortCol, setSortCol] = useState<DepositSortCol>('depositDate');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const rows = useMemo(() => {
    const list = deposits.filter((d) => depositMatchesQuery(d, query));
    const mult = sortDir === 'asc' ? 1 : -1;
    return [...list].sort((a, b) => {
      const va = depositSortKey(a, sortCol);
      const vb = depositSortKey(b, sortCol);
      if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * mult;
      return String(va).localeCompare(String(vb)) * mult;
    });
  }, [deposits, query, sortCol, sortDir]);

  const toggleSort = (col: DepositSortCol) => {
    if (sortCol === col) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortCol(col);
      setSortDir(col === 'depositDate' ? 'desc' : 'asc');
    }
  };

  const exportCsv = () => {
    const headers = ['Deposit date', 'Amount', 'Payor', 'Payor type', 'Eligible', 'Summary', 'Exclusion'];
    const body = rows.map((d) => [
      d.depositDate,
      d.amount ?? '',
      d.payorDisplay ?? '',
      d.payorType,
      d.isEligible ? 'Yes' : 'No',
      d.summaryLine,
      d.exclusionReason ?? '',
    ]);
    downloadCsv(`income-deposits-${data.id}.csv`, toCsv(headers, body));
  };

  return (
    <>
      {deposits.length > 0 ? (
        <>
          <Card variant="outlined" sx={{ mb: 1.5 }}>
            <CardContent sx={{ py: 1.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
                Eligible vs excluded (canonical deposits)
              </Typography>
              <Grid container spacing={1}>
                <Grid size={{ xs: 6, sm: 4 }}>
                  <Typography variant="caption" color="text.secondary">
                    Eligible
                  </Typography>
                  <Typography variant="body2">
                    {eligibility.eligibleCount} deposits · {formatCad(eligibility.eligibleSum)}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 6, sm: 4 }}>
                  <Typography variant="caption" color="text.secondary">
                    Excluded
                  </Typography>
                  <Typography variant="body2">
                    {eligibility.excludedCount} deposits · {formatCad(eligibility.excludedSum)}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <Typography variant="caption" color="text.secondary">
                    Top exclusion reasons
                  </Typography>
                  <Typography variant="body2">
                    {eligibility.topExclusionReasons.length === 0
                      ? '—'
                      : eligibility.topExclusionReasons.map((x) => `${x.reason} (${x.count})`).join(' · ')}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Card variant="outlined" sx={{ mb: 1.5 }}>
            <CardContent sx={{ py: 1.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.5 }}>
                Payor-grouped view (heuristic)
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                Groups use displayed payor + payor type. Cadence is the median gap between deposit dates for that
                group (needs ≥2 dated deposits); it is a rough hint only.
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Payor</TableCell>
                      <TableCell align="right">Count</TableCell>
                      <TableCell align="right">Sum</TableCell>
                      <TableCell align="right">Eligible sum</TableCell>
                      <TableCell align="right">Excluded sum</TableCell>
                      <TableCell>Date span</TableCell>
                      <TableCell align="right">Median gap (days)</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {payorGroups.map((g) => (
                      <TableRow key={g.key}>
                        <TableCell>
                          <Typography variant="body2">{g.displayName}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {g.payorType}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">{g.depositCount}</TableCell>
                        <TableCell align="right">{formatCad(g.sum)}</TableCell>
                        <TableCell align="right">{formatCad(g.eligibleSum)}</TableCell>
                        <TableCell align="right">{formatCad(g.excludedSum)}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap', fontSize: '0.75rem' }}>
                          {g.dateMin && g.dateMax ? (
                            <>
                              {formatIso(g.dateMin)} → {formatIso(g.dateMax)}
                            </>
                          ) : (
                            '—'
                          )}
                        </TableCell>
                        <TableCell align="right">
                          {g.medianGapDays !== null ? g.medianGapDays.toFixed(1) : '—'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </>
      ) : null}

      {data.transactionMeta?.incomeDepositsCapped ? (
        <Alert severity="info" sx={{ mb: 1 }}>
          Showing the most recent {data.transactionMeta.incomeDepositsReturned} deposits (server cap).
        </Alert>
      ) : null}

      {deposits.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No canonical deposits linked to this session.
        </Typography>
      ) : (
        <>
          <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 1.5 }} alignItems="center">
            <TextField
              size="small"
              label="Search table"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              sx={{ minWidth: 200, flex: '1 1 200px' }}
            />
            <Button variant="outlined" size="small" onClick={exportCsv}>
              Export CSV ({rows.length})
            </Button>
          </Stack>
          <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 360 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sortDirection={sortCol === 'depositDate' ? sortDir : false}>
                    <TableSortLabel
                      active={sortCol === 'depositDate'}
                      direction={sortCol === 'depositDate' ? sortDir : 'asc'}
                      onClick={() => toggleSort('depositDate')}
                    >
                      Deposit date
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right" sortDirection={sortCol === 'amount' ? sortDir : false}>
                    <TableSortLabel
                      active={sortCol === 'amount'}
                      direction={sortCol === 'amount' ? sortDir : 'asc'}
                      onClick={() => toggleSort('amount')}
                      sx={{ justifyContent: 'flex-end' }}
                    >
                      Amount
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={sortCol === 'payor' ? sortDir : false}>
                    <TableSortLabel
                      active={sortCol === 'payor'}
                      direction={sortCol === 'payor' ? sortDir : 'asc'}
                      onClick={() => toggleSort('payor')}
                    >
                      Payor
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={sortCol === 'eligible' ? sortDir : false}>
                    <TableSortLabel
                      active={sortCol === 'eligible'}
                      direction={sortCol === 'eligible' ? sortDir : 'asc'}
                      onClick={() => toggleSort('eligible')}
                    >
                      Eligible
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ minWidth: 220 }} sortDirection={sortCol === 'summary' ? sortDir : false}>
                    <TableSortLabel
                      active={sortCol === 'summary'}
                      direction={sortCol === 'summary' ? sortDir : 'asc'}
                      onClick={() => toggleSort('summary')}
                    >
                      Summary
                    </TableSortLabel>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((d) => {
                  const payorRawCaption = incomeDepositPayorRawCaption(d);
                  return (
                  <TableRow key={d.id} hover selected={d.isInvalid || !d.isEligible}>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatIso(d.depositDate)}</TableCell>
                    <TableCell align="right">{formatCad(d.amount)}</TableCell>
                    <TableCell>
                      <Typography variant="body2">{incomeDepositPayorLabel(d)}</Typography>
                      {payorRawCaption ? (
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ wordBreak: 'break-word' }}>
                          {payorRawCaption}
                        </Typography>
                      ) : null}
                      <Typography variant="caption" color="text.secondary" display="block">
                        {d.payorType}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={d.isEligible ? 'Yes' : 'No'}
                        color={d.isEligible ? 'success' : 'warning'}
                        variant={d.isEligible ? 'filled' : 'outlined'}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{d.summaryLine}</Typography>
                      {d.exclusionReason ? (
                        <Typography variant="caption" color="warning.main" display="block">
                          {d.exclusionReason}
                        </Typography>
                      ) : null}
                      {d.isInvalid ? (
                        <Chip size="small" color="error" label={d.invalidReason || 'Invalid'} sx={{ mt: 0.5 }} />
                      ) : null}
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </>
  );
}

function DetailPane({
  data,
  onClose,
  onRequestFullscreen,
}: {
  data: IncomeDashboardDetail;
  onClose: () => void;
  onRequestFullscreen?: () => void;
}) {
  const m = data.metrics;

  return (
    <Stack spacing={2}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            Income verification record
          </Typography>
          <Typography variant="caption" color="text.secondary">
            ID {data.id}
          </Typography>
        </Box>
        <Stack direction="row" spacing={0.5}>
          {onRequestFullscreen ? (
            <IconButton size="small" onClick={onRequestFullscreen} aria-label="Fullscreen detail">
              <OpenInFullRoundedIcon />
            </IconButton>
          ) : null}
          <IconButton size="small" onClick={onClose} aria-label="Close detail">
            <CloseRoundedIcon />
          </IconButton>
        </Stack>
      </Box>

      <Card variant="outlined" sx={{ borderColor: 'primary.main', borderWidth: 1 }}>
        <CardContent>
          <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1.25 }}>
            Signals at a glance
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
            <Chip size="small" label={`Final: ${data.finalStatus}`} color={statusColor(data.finalStatus)} />
            <Chip size="small" label={`Verdict: ${data.verdict ?? '—'}`} />
            <Chip size="small" label={`Confidence: ${data.confidenceScore ?? '—'}`} />
            <Chip size="small" label={`Attempt: ${data.attemptNumber ?? '—'}`} />
          </Stack>

          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
            Pipeline outcomes (persisted)
          </Typography>
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mb: 1.25 }}>
            <Chip size="small" variant="outlined" label={`Recurrence: ${data.outcomes.recurrenceOutcome ?? '—'}`} />
            <Chip size="small" variant="outlined" label={`Stability: ${data.outcomes.stabilityOutcome ?? '—'}`} />
            <Chip size="small" variant="outlined" label={`Arbitration: ${data.outcomes.arbitrationOutcome ?? '—'}`} />
          </Stack>

          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
            Gates
          </Typography>
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mb: 1.25 }}>
            {GATES.map((gate) => (
              <Tooltip key={gate} title={data.gateMessages[gate] || ''}>
                <Chip
                  size="small"
                  label={`${gate} ${data.gates[gate]}`}
                  color={gateColor(data.gates[gate])}
                  variant={data.gates[gate] === 'NOT_RUN' ? 'outlined' : 'filled'}
                />
              </Tooltip>
            ))}
          </Stack>

          <Grid container spacing={1}>
            <Grid size={{ xs: 6, md: 4 }}>
              <Typography variant="caption" color="text.secondary">
                Normalized monthly income
              </Typography>
              <Typography variant="body2">{m?.normalizedMonthlyIncome ?? '—'}</Typography>
            </Grid>
            <Grid size={{ xs: 6, md: 4 }}>
              <Typography variant="caption" color="text.secondary">
                Stability score
              </Typography>
              <Typography variant="body2">{m?.stabilityScore ?? '—'}</Typography>
            </Grid>
            <Grid size={{ xs: 6, md: 4 }}>
              <Typography variant="caption" color="text.secondary">
                Volatility index
              </Typography>
              <Typography variant="body2">{m?.volatilityIndex ?? '—'}</Typography>
            </Grid>
            <Grid size={{ xs: 6, md: 4 }}>
              <Typography variant="caption" color="text.secondary">
                Recurrence confidence
              </Typography>
              <Typography variant="body2">{m?.recurrenceConfidence ?? '—'}</Typography>
            </Grid>
            <Grid size={{ xs: 6, md: 4 }}>
              <Typography variant="caption" color="text.secondary">
                Frequency classification
              </Typography>
              <Typography variant="body2">{m?.frequencyClassification ?? '—'}</Typography>
            </Grid>
            <Grid size={{ xs: 6, md: 4 }}>
              <Typography variant="caption" color="text.secondary">
                Primary payor
              </Typography>
              <Typography variant="body2">{m?.primaryPayor ?? '—'}</Typography>
            </Grid>
          </Grid>

          {data.failReasons.length > 0 ? (
            <Alert severity="warning" sx={{ mt: 1.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                Fail / review reasons
              </Typography>
              {data.failReasons.join(' | ')}
            </Alert>
          ) : null}
        </CardContent>
      </Card>

      {(data.gateFlowSteps ?? []).length > 0 ? (
        <Alert severity="info" icon={false}>
          <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.75 }}>
            How gates relate to what you see
          </Typography>
          <Stack spacing={0.75}>
            {(data.gateFlowSteps ?? []).map((step) => (
              <Typography key={step.gateKey} variant="body2" color="text.secondary">
                <strong>{step.shortCode}</strong> ({step.gateKey}): {step.whatHappens}{' '}
                <Typography component="span" variant="body2" color="text.secondary">
                  Outcomes: {step.outcomesExplained}
                </Typography>
              </Typography>
            ))}
          </Stack>
        </Alert>
      ) : null}

      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography sx={{ fontWeight: 800 }}>Bank transactions (IBV)</Typography>
            <Chip size="small" label={(data.bankTransactions ?? []).length} variant="outlined" />
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 0 }}>
          <BankTransactionsSection data={data} />
        </AccordionDetails>
      </Accordion>

      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography sx={{ fontWeight: 800 }}>Income deposits (canonical)</Typography>
            <Chip size="small" label={(data.incomeDeposits ?? []).length} variant="outlined" />
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 0 }}>
          <IncomeDepositsSection data={data} />
        </AccordionDetails>
      </Accordion>
    </Stack>
  );
}

export function IncomeVerificationDashboard() {
  const theme = useTheme();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [latestOnly, setLatestOnly] = useState(true);
  const [applicationIdQ, setApplicationIdQ] = useState('');
  const [ccidQ, setCcidQ] = useState('');
  const [verifiedFrom, setVerifiedFrom] = useState('');
  const [verifiedTo, setVerifiedTo] = useState('');
  const [sortBy, setSortBy] = useState('VERIFIED_AT_DESC');
  const [finalFilter, setFinalFilter] = useState<string | undefined>(undefined);
  const [applied, setApplied] = useState({
    applicationIdContains: '',
    ccidContains: '',
    verifiedFromIso: '',
    verifiedToIso: '',
  });
  const [detailId, setDetailId] = useState<string | null>(null);
  const [detailFullscreen, setDetailFullscreen] = useState(false);

  const summaryQ = useQuery({
    queryKey: ['incomeDashboardSummary'],
    queryFn: () => incomeVerificationDashboardApi.summary(),
  });

  const listQ = useQuery({
    queryKey: ['incomeDashboardList', page, rowsPerPage, latestOnly, finalFilter, applied, sortBy],
    queryFn: () =>
      incomeVerificationDashboardApi.list({
        limit: rowsPerPage,
        offset: page * rowsPerPage,
        latestPerApplicationOnly: latestOnly,
        finalStatus: finalFilter,
        applicationIdContains: applied.applicationIdContains || undefined,
        ccidContains: applied.ccidContains || undefined,
        verifiedFrom: applied.verifiedFromIso || undefined,
        verifiedTo: applied.verifiedToIso || undefined,
        sortBy,
      }),
  });

  const detailQ = useQuery({
    queryKey: ['incomeDashboardDetail', detailId],
    queryFn: () => incomeVerificationDashboardApi.detail(detailId!),
    enabled: !!detailId,
  });

  const listControls = summaryQ.data?.listControls;
  const outcomeQuickFilters = listControls?.outcomeQuickFilters ?? [];
  const sortOptions = listControls?.sortOptions ?? [];

  const applyFilters = () => {
    setApplied({
      applicationIdContains: applicationIdQ.trim(),
      ccidContains: ccidQ.trim(),
      verifiedFromIso: verifiedFrom.trim(),
      verifiedToIso: verifiedTo.trim(),
    });
    setPage(0);
  };

  const tiles = summaryQ.data?.outcomeTiles ?? [];

  return (
    <Box>
      <Accordion defaultExpanded sx={{ mb: 2, borderRadius: 2, '&:before': { display: 'none' }, border: 1, borderColor: 'divider' }}>
        <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />}>
          <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
            Income doctrine flow
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 0 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            This dashboard reads persisted income verification snapshots and audit trail by
            session/application. Use rows to open detail and inspect gate outcomes + reasons.
          </Typography>
          <Grid container spacing={1.25}>
            {(summaryQ.data?.gateFlowSteps ?? []).map((step) => (
              <Grid key={step.gateKey} size={{ xs: 12, md: 4 }}>
                <Card variant="outlined">
                  <CardContent sx={{ py: 1.5 }}>
                    <Typography variant="overline" color="primary" sx={{ fontWeight: 800 }}>
                      {step.shortCode} — {step.gateKey}
                    </Typography>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 0.5 }}>
                      {step.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                      {step.whatHappens}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </AccordionDetails>
      </Accordion>

      <Stack direction="row" alignItems="baseline" justifyContent="space-between" flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 800,
            letterSpacing: -0.02,
            background: `linear-gradient(120deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          }}
        >
          Income verification dashboard
        </Typography>
      </Stack>

      {summaryQ.isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={32} />
        </Box>
      ) : summaryQ.isError ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {incomeError(summaryQ.error)}
        </Alert>
      ) : (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {tiles.map((tile) => (
            <Grid key={tile.id} size={{ xs: 12, sm: 6, md: 3 }}>
              <Card variant="outlined">
                <CardActionArea
                  onClick={() => {
                    setPage(0);
                    setFinalFilter(tile.finalStatus ?? undefined);
                  }}
                >
                  <CardContent sx={{ py: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 700 }}>
                      {tile.title}
                    </Typography>
                    <Typography variant="h3" sx={{ mt: 0.75, fontWeight: 900, lineHeight: 1.1 }}>
                      {tile.count}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                      {tile.subtitle}
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <Card variant="outlined" sx={{ mb: 2, borderRadius: 2 }}>
        <CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
            Filters & sorting
          </Typography>
          <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 1.5 }}>
            {outcomeQuickFilters.map((opt) => {
              const isAll = opt.finalStatus === null || opt.finalStatus === undefined;
              const selected = isAll ? finalFilter === undefined : finalFilter === opt.finalStatus;
              return (
                <Chip
                  key={opt.label}
                  label={opt.label}
                  color={selected ? 'primary' : 'default'}
                  variant={selected ? 'filled' : 'outlined'}
                  onClick={() => {
                    setPage(0);
                    setFinalFilter(isAll ? undefined : opt.finalStatus ?? undefined);
                  }}
                />
              );
            })}
          </Stack>
          <Grid container spacing={2} sx={{ mb: 1 }}>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                size="small"
                label="Application id contains"
                value={applicationIdQ}
                onChange={(e) => setApplicationIdQ(e.target.value)}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                size="small"
                label="CCID contains"
                value={ccidQ}
                onChange={(e) => setCcidQ(e.target.value)}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Sort rows by</InputLabel>
                <Select
                  label="Sort rows by"
                  value={sortBy}
                  onChange={(e) => {
                    setSortBy(String(e.target.value));
                    setPage(0);
                  }}
                >
                  {sortOptions.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                size="small"
                label="Verified after (ISO)"
                value={verifiedFrom}
                onChange={(e) => setVerifiedFrom(e.target.value)}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                size="small"
                label="Verified before (ISO)"
                value={verifiedTo}
                onChange={(e) => setVerifiedTo(e.target.value)}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Button variant="contained" onClick={applyFilters}>
                Apply filters
              </Button>
            </Grid>
          </Grid>
          <Divider sx={{ my: 1 }} />
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="body2" color="text.secondary">Latest attempt per application</Typography>
            <Switch
              checked={latestOnly}
              onChange={(_, checked) => {
                setLatestOnly(checked);
                setPage(0);
              }}
            />
          </Stack>
        </CardContent>
      </Card>

      {listQ.isError ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {incomeError(listQ.error)}
        </Alert>
      ) : null}

      <Card
        variant="outlined"
        sx={{
          borderRadius: 2,
          overflow: 'hidden',
          borderColor: alpha(theme.palette.primary.main, theme.palette.mode === 'light' ? 0.22 : 0.35),
        }}
      >
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 44 }} />
              <TableCell>Decision</TableCell>
              <TableCell>Gates</TableCell>
              <TableCell>Application ID</TableCell>
              <TableCell>Session · CCID</TableCell>
              <TableCell align="right">Attempt</TableCell>
              <TableCell align="right">Income</TableCell>
              <TableCell align="right">Recurrence</TableCell>
              <TableCell>Frequency</TableCell>
              <TableCell>Verified</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {listQ.isLoading ? (
              <TableRow>
                <TableCell colSpan={10}>
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress size={28} />
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              (listQ.data?.items ?? []).map((row: IncomeDashboardListItem) => (
                <TableRow key={row.id} hover>
                  <TableCell>
                    <IconButton size="small" onClick={() => setDetailId(row.id)}>
                      <OpenInNewOutlinedIcon fontSize="inherit" />
                    </IconButton>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={row.finalStatus}
                      color={statusColor(row.finalStatus)}
                      sx={{ fontWeight: 700 }}
                    />
                  </TableCell>
                  <TableCell sx={{ minWidth: 240 }}>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                      {GATES.map((g) => (
                        <Tooltip key={`${row.id}-${g}`} title={row.gateMessages[g] || ''}>
                          <Chip
                            size="small"
                            label={`${g} ${row.gates[g]}`}
                            color={gateColor(row.gates[g])}
                            variant={row.gates[g] === 'NOT_RUN' ? 'outlined' : 'filled'}
                          />
                        </Tooltip>
                      ))}
                    </Stack>
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'ui-monospace, monospace', fontWeight: 700 }}>
                    {row.applicationId}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'ui-monospace, monospace', fontWeight: 700 }}>
                      {row.sessionId}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {row.ccid ?? '—'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">{row.attemptNumber ?? '—'}</TableCell>
                  <TableCell align="right">{row.metrics?.normalizedMonthlyIncome ?? '—'}</TableCell>
                  <TableCell align="right">{row.metrics?.recurrenceConfidence ?? '—'}</TableCell>
                  <TableCell sx={{ maxWidth: 160 }}>
                    <Typography variant="body2" noWrap title={row.metrics?.frequencyClassification ?? ''}>
                      {row.metrics?.frequencyClassification ?? '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>{formatIso(row.verifiedAt)}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={listQ.data?.total ?? 0}
          page={page}
          onPageChange={(_e, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(Number(e.target.value));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Card>

      <Drawer
        anchor="right"
        open={!!detailId}
        onClose={() => {
          setDetailId(null);
          setDetailFullscreen(false);
        }}
        PaperProps={{ sx: { width: { xs: 1, sm: 540, md: 680 }, maxWidth: '100vw', p: 2 } }}
      >
        {detailQ.isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : detailQ.isError ? (
          <Alert severity="error">{incomeError(detailQ.error)}</Alert>
        ) : detailQ.data ? (
          <DetailPane
            data={detailQ.data}
            onClose={() => {
              setDetailId(null);
              setDetailFullscreen(false);
            }}
            onRequestFullscreen={() => setDetailFullscreen(true)}
          />
        ) : null}
      </Drawer>

      <Dialog
        fullScreen
        open={detailFullscreen && !!detailId && !!detailQ.data}
        onClose={() => setDetailFullscreen(false)}
      >
        <Box sx={{ p: 2 }}>
          {detailQ.data ? (
            <DetailPane data={detailQ.data} onClose={() => setDetailFullscreen(false)} />
          ) : null}
        </Box>
      </Dialog>
    </Box>
  );
}
